import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

const PortalRings = () => {
    const rings = [
        { size: 60, delay: 0, color: "#6C63FF", dur: 500 },
        { size: 130, delay: 80, color: "#9B5DE5", dur: 550 },
        { size: 220, delay: 160, color: "#F72585", dur: 600 },
        { size: 350, delay: 240, color: "#4CC9F0", dur: 650 },
    ];
    return (
        <div style={{
            position: "fixed", inset: 0, display: "flex",
            alignItems: "center", justifyContent: "center",
            zIndex: 9999, pointerEvents: "none",
        }}>
            {rings.map((r, i) => (
                <div key={i} style={{
                    position: "absolute",
                    width: r.size, height: r.size,
                    borderRadius: "50%",
                    border: `2px solid ${r.color}`,
                    animation: `portalRing ${r.dur}ms ${r.delay}ms cubic-bezier(.2,0,.2,1) forwards`,
                }} />
            ))}
            <style>{`
        @keyframes portalRing {
          0%   { transform: scale(0); opacity: 0.9; }
          60%  { transform: scale(1); opacity: 0.8; }
          100% { transform: scale(1.8); opacity: 0; }
        }
      `}</style>
        </div>
    );
};

const NewConversationsIframeScreen = () => {
    const iframeRef = useRef(null);
    const canvasRef = useRef(null);
    const [showPortal, setShowPortal] = useState(true);
    const [iframeVisible, setIframeVisible] = useState(false);
    const animFrameRef = useRef(null);
    const history = useHistory();
    const host = window.location.hostname;

    const key = Object.keys(sessionStorage).find(k =>
        k.startsWith("oidc.user:")
    );
    const userData = key ? JSON.parse(sessionStorage.getItem(key)) : null;
    const groupId = userData?.profile?.group_id ?? localStorage.getItem("groupId");

    const WATCHED_KEYS = [
        "currentPropertyId",
        "groupId",
        "timeZone",
        "service_provider",
        "isVoucherEngine",
        "email",
    ];

    const sendData = () => {
        iframeRef.current?.contentWindow?.postMessage(
            {
                type: "Data_loaded",
                currentPropertyId: localStorage.getItem("currentPropertyId"),
                email: localStorage.getItem("email"),
                groupId: groupId,
                isVoucherEngine: localStorage.getItem("isVoucherEngine"),
                timeZone: localStorage.getItem("timeZone"),
                serviceProvider: localStorage.getItem("service_provider"),
            },
            "*"
        );
    };

    let onboardAppUrl = "";

    if (host.includes("myma.ai")) {
        onboardAppUrl = process.env.REACT_APP_ONBOARD_APP_URL_MYMA;
    } else if (host.includes("bookmebob.com")) {
        onboardAppUrl = process.env.REACT_APP_ONBOARD_APP_URL_BOOKMEBOB;
    } else if (host.includes("concierge.synxis.com")) {
        onboardAppUrl = process.env.REACT_APP_ONBOARD_APP_URL_SYNXIS;
    } else {
        throw new Error(`Unsupported host: ${host}`);
    }
    const parentSearchParams = new URLSearchParams(window.location.search);
    const assistantId = parentSearchParams.get("assistantId") || "";

    const contextParams = new URLSearchParams({
        pid: localStorage.getItem("currentPropertyId") || "",
        gid: groupId || "",
        email: localStorage.getItem("email") || "",
        tz: localStorage.getItem("timeZone") || "",
        sp: localStorage.getItem("service_provider") || "",
        ive: localStorage.getItem("isVoucherEngine") || "",
        assistantId: assistantId,
    }).toString();
    // Particle burst effect
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        const ctx = canvas.getContext("2d");
        const cx = canvas.width / 2;
        const cy = canvas.height / 2;

        const colors = ["#6C63FF", "#F72585", "#4CC9F0", "#9B5DE5", "#ffffff"];
        let particles = Array.from({ length: 80 }, () => {
            const angle = Math.random() * Math.PI * 2;
            const speed = 2 + Math.random() * 6;
            return {
                x: cx, y: cy,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                r: 1.5 + Math.random() * 3,
                color: colors[Math.floor(Math.random() * colors.length)],
                life: 1,
                decay: 0.018 + Math.random() * 0.025,
            };
        });

        function tick() {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            particles = particles.filter((p) => p.life > 0);
            for (const p of particles) {
                ctx.globalAlpha = p.life;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = p.color;
                ctx.fill();
                p.x += p.vx;
                p.y += p.vy;
                p.vy += 0.07;
                p.life -= p.decay;
            }
            ctx.globalAlpha = 1;
            if (particles.length > 0) {
                animFrameRef.current = requestAnimationFrame(tick);
            }
        }
        animFrameRef.current = requestAnimationFrame(tick);

        // Iframe fade-in after burst
        const t1 = setTimeout(() => setIframeVisible(true), 600);
        // Unmount portal overlay
        const t2 = setTimeout(() => setShowPortal(false), 1000);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            cancelAnimationFrame(animFrameRef.current);
        };
    }, []);

    // postMessage logic — unchanged
    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data.type === "IFRAME_READY") sendData();
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    useEffect(() => {
        let lastSnapshot = WATCHED_KEYS.map((k) => localStorage.getItem(k)).join("|");

        const intervalId = setInterval(() => {
            const currentSnapshot = WATCHED_KEYS.map((k) => localStorage.getItem(k)).join("|");
            if (currentSnapshot !== lastSnapshot) {
                lastSnapshot = currentSnapshot;
                sendData();
            }
        }, 400);

        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
        const handleNavigationMessage = (event) => {
            if (event.data?.type === "NAVIGATE_TO_INTEGRATION" && event.data?.url) {
                history.push(event.data.url);
            }
        };
        window.addEventListener("message", handleNavigationMessage);
        return () => window.removeEventListener("message", handleNavigationMessage);
    }, [history]);
    // ================================================================================================================

    return (
        <div className="content-area">
            {showPortal && (
                <>
                    <PortalRings />
                    <canvas
                        ref={canvasRef}
                        style={{
                            position: "fixed",
                            inset: 0,
                            pointerEvents: "none",
                            zIndex: 9998,
                        }}
                    />
                </>
            )}
            <iframe
                ref={iframeRef}
                src={`${onboardAppUrl}/?module=conversationFlow&${contextParams}`}
                title="Switch to New Version"
                allow="microphone"
                style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    position: "fixed",
                    margin: "-60px",
                    marginTop: "-23px",
                    opacity: iframeVisible ? 1 : 0,
                    transition: "opacity 0.6s ease",
                }}
            />
        </div>
    );
};

export default NewConversationsIframeScreen;